<style scoped>
section.main-section {
  height: 100%;
}
</style>

<template>
  <div class="main-wrapper">
    <section class="section main-section bg-gradient-default min-vh-100 pt-3">
      <main class="container card shadow shadow-lg--hover mb-3">
        <Header :title="template ? template.name : 'loading...'" />
        <b-row class="mb-3">
          <b-col cols="6">
            <b-button variant="default" @click.stop="navigateBack()" size="sm">
              <b-icon-chevron-left scale="1.5" />
            </b-button>
          </b-col>
          <b-col cols="6">
            <b-button-toolbar class="float-right">
              <b-button-group>
                <b-button variant="primary" size="sm" @click="refresh">
                  <b-icon-arrow-clockwise scale="1.3" />
                </b-button>
              </b-button-group>
            </b-button-toolbar>
          </b-col>
        </b-row>
        <b-row class="mb-1">
          <b-col>
            <div class="form-group">
              <div class="input-group">
                <flat-pickr
                  v-model="date"
                  :config="config"
                  class="form-control"
                  @on-open="setDatePickerAsVisible"
                  @on-close="setDatePickerAsHidden"
                  placeholder="Filtrar por fechas"
                  name="date"
                >
                </flat-pickr>
                <b-input-group-append>
                  <b-button
                    variant="default"
                    type="button"
                    title="Toggle"
                    data-toggle
                  >
                    <b-icon-calendar>
                      <span aria-hidden="true" class="sr-only">Toggle</span>
                    </b-icon-calendar>
                  </b-button>
                </b-input-group-append>
              </div>
            </div>
            <b-form-group>
              <b-input-group>
                <b-form-input
                  v-model="searchText"
                  placeholder="Filtrar por..."
                  type="text"
                  debounce="500"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
        <b-overlay
          :show="templateEntries.length === 0 || loading"
          variant="white"
        >
          <b-row>
            <b-col cols="12">
              <b-list-group class="mb-3">
                <b-list-group-item
                  button
                  v-for="(entry, key) in sortedEntries"
                  :key="key"
                  class="d-flex justify-content-between align-items-center"
                  @click.stop="navigateToEntry(entry)"
                >
                  <span>
                    <strong
                      >{{ entry.name }}
                      <small>Revision {{ entry.revision }}</small></strong
                    >
                    <div>
                      <p>
                        {{ entry.completed ? "Completado" : "Creada" }} por
                        {{ entry.createdBy }}
                      </p>
                      <small>
                        <b-badge
                          :variant="entry.completed ? 'success' : 'warning'"
                        >
                          <b-icon-exclamation-circle
                            v-if="!entry.completed"
                            scale="2"
                          ></b-icon-exclamation-circle>
                          <b-icon-check
                            v-if="entry.completed"
                            scale="2"
                          ></b-icon-check>
                        </b-badge>
                      </small>
                      <small class="text-muted">
                        Creado {{ entry.createdOn | timestamp }} y modificado
                        {{ entry.lastUpdatedAt | timestamp }}
                      </small>
                    </div>
                  </span>
                  <b-icon-chevron-right scale="2"></b-icon-chevron-right>
                </b-list-group-item>
              </b-list-group>
            </b-col>
          </b-row>
          <template v-slot:overlay>
            <div>
              <p v-if="!loading">
                No se encontró nigúna entrada para las fechas especificadas,
                intente otra fecha.
              </p>
              <b-spinner v-else ref="spinner" type="grow"></b-spinner>
            </div>
          </template>
        </b-overlay>
        <b-row class="mb-3">
          <b-col cols="12">
            <b-button-toolbar>
              <b-button-group class="mx-1">
                <b-button
                  variant="default"
                  @click.stop="navigateBack()"
                  size="sm"
                >
                  <b-icon-chevron-left scale="1.5" />
                </b-button>
              </b-button-group>
            </b-button-toolbar>
          </b-col>
        </b-row>
      </main>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { isEmpty, isNil, sortWith, descend, prop } from "ramda";
import moment from "moment";
import Header from "../Header";

moment.locale("es");

const sortByTimestampAndCreator = sortWith([
  descend(prop("createdOn")),
  descend(prop("createdBy")),
]);

const ONE_WEEK_IN_MS = 604800000 * 2;

export default {
  name: "Entries",
  components: {
    Header,
  },
  data() {
    return {
      loading: true,
      templateEntries: [],
      template: {}, // Initial value
      cursor: null,
      date: [Date.now() - ONE_WEEK_IN_MS, Date.now()],
      isDatePickerOpen: false,
      searchText: "",
      config: {
        mode: "range",
        wrap: true, // set wrap to true only when using 'input-group'
        altFormat: "M j, Y",
        dateFormat: "d-m-Y",
      },
    };
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.scroll();
  },
  computed: {
    ...mapGetters("checklists", ["getEntries", "getAllEntriesFromTemplate"]),
    ...mapGetters("templates", ["getTemplate"]),
    sortedEntries() {
      let sortedEntries = sortByTimestampAndCreator(this.templateEntries);
      // all the filter stuff should be done as API call
      if (!isEmpty(this.searchText)) {
        sortedEntries = sortedEntries.filter((entry) => {
          const matchedAuthor = entry.createdBy.includes(this.searchText);
          const matchedRevision = entry.revision === parseInt(this.searchText);
          const matchedName = entry.name.includes(this.searchText);
          return matchedAuthor || matchedName || matchedRevision;
        });
      }

      return sortedEntries;
    },
  },
  methods: {
    ...mapActions("templates", ["fetchTemplate"]),
    ...mapActions("checklists", [
      "fetchCheckListEntriesFromTemplate",
      "fetchAllEntriesFromCheckList",
    ]),
    scroll() {
      window.onscroll = () => {
        const bottomOfWindow =
          document.documentElement.scrollTop + window.innerHeight ===
          document.documentElement.offsetHeight;
        if (bottomOfWindow && this.cursor) {
          this.refresh();
        }
      };
    },
    async fetchData() {
      this.loading = true;

      const dateContext = this.parseDate();

      let { templateId, rev } = this.$route.params;
      if (rev) {
        rev = parseInt(rev);
        if (isNil(this.getTemplate(templateId, rev))) {
          await this.fetchTemplate({ templateId, revision: rev });
        }
        this.template = this.getTemplate(templateId, rev);

        if (isNil(this.getEntries(templateId, rev))) {
          // we want to get the entries for that specific revision
          this.cursor = await this.fetchCheckListEntriesFromTemplate({
            templateId,
            revision: rev,
            ...dateContext,
            cursor: this.cursor,
          });
        }

        this.templateEntries = this.getEntries(templateId, rev);
      } else {
        // get all the entries
        this.cursor = await this.fetchAllEntriesFromCheckList({
          templateId,
          cursor: this.cursor,
          ...dateContext,
        });
        this.templateEntries = this.getAllEntriesFromTemplate(templateId);
      }

      this.loading = false;
    },
    async refresh() {
      this.loading = true;

      const dateContext = this.parseDate();

      let { templateId, rev } = this.$route.params;
      if (rev) {
        rev = parseInt(rev);
        await this.fetchTemplate({ templateId, revision: rev });
        this.template = this.getTemplate(templateId, rev);

        // we want to get the entries for that specific revision
        this.cursor = await this.fetchCheckListEntriesFromTemplate({
          templateId,
          revision: rev,
          cursor: this.cursor,
          ...dateContext,
        });

        this.templateEntries = this.getEntries(templateId, rev);
      } else {
        // get all the entries
        this.cursor = await this.fetchAllEntriesFromCheckList({
          templateId,
          ...dateContext,
          cursor: this.cursor,
        });
        this.templateEntries = this.getAllEntriesFromTemplate(templateId);
      }

      this.loading = false;
    },
    parseDate() {
      if (this.date) {
        let from, to;
        if (typeof this.date === "string") {
          [from, to] = this.date.split(" to ");

          const value = {};
          if (from) value.from = moment(from, "DD-MM-YYYY").valueOf();
          if (to) {
            value.to = moment(to, "DD-MM-YYYY").valueOf();
          } else {
            const tmpDate = new Date(value.from);
            tmpDate.setHours(23, 59);
            value.to = tmpDate.valueOf();
          }
          return value;
        } else if (Array.isArray(this.date)) {
          return { from: this.date[0], to: this.date[1] };
        }
      }

      return {};
    },
    navigateToEntry(entry) {
      const { templateId } = this.$route.params;
      const rev = entry.revision;
      this.$router.push({
        path: `/checklists/${templateId}/${rev}/entries/${entry.entryId}`,
      });
    },
    navigateBack() {
      window.history.length > 1 ? this.$router.go(-1) : this.$router.push("/");
    },
    setDatePickerAsHidden() {
      this.isDatePickerOpen = false;
    },
    setDatePickerAsVisible() {
      this.isDatePickerOpen = true;
    },
  },
  filters: {
    timestamp(value) {
      return moment(value).fromNow();
    },
  },
  watch: {
    date(newValue, oldValue) {
      if (
        (typeof newValue === "string" && newValue.includes(" to ")) ||
        this.isDatePickerOpen
      ) {
        console.log(`${newValue} - ${oldValue} - ${this.isDatePickerOpen}`);
        this.refresh();
      }
    },
  },
};
</script>
